"use client";

import {
  buildProfileDisplayLines,
  ProfileReadOnlyLines,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import type { RetailCompany } from "@/lib/cat/retail-client.types";
import { parseMdmContactUse } from "@/lib/cat/mdm-map-contacts";

type DisplayLine = { label: string; value: string };

function optionalSiretLine(
  siret: string | undefined | null,
): DisplayLine | null {
  const value = siret?.trim();
  if (!value) return null;
  return { label: "SIRET", value };
}

function inseeProfileFromCompany(company: RetailCompany) {
  return {
    companyDenomination: company.inseeDenomination ?? "",
    apeCode: company.inseeApe ?? company.ape,
    email: "",
    addressLine: company.inseeAddressLine ?? "",
    addressFloorApartment: company.inseeAddressFloorApartment ?? "",
    addressBuildingResidence: company.inseeAddressBuildingResidence ?? "",
    postBox: company.inseePostBox ?? "",
    postalCode: company.inseePostalCode ?? "",
    city: company.inseeCity ?? "",
    fixedPhone: { value: "", use: "unknown" as const },
    mobilePhone: { value: "", use: "unknown" as const },
  };
}

function refPersProfileFromCompany(company: RetailCompany) {
  return {
    companyDenomination: company.mdmDenomination ?? "",
    apeCode: company.mdmApe ?? "",
    email: company.mdmEmail ?? "",
    addressLine: company.mdmAddressLine ?? "",
    addressFloorApartment: company.mdmAddressFloorApartment ?? "",
    addressBuildingResidence: company.mdmAddressBuildingResidence ?? "",
    postBox: company.mdmPostBox ?? "",
    postalCode: company.mdmPostalCode ?? "",
    city: company.mdmCity ?? "",
    fixedPhone: {
      value: company.mdmFixedPhone ?? "",
      use: parseMdmContactUse(company.mdmFixedPhoneUse),
    },
    mobilePhone: {
      value: company.mdmMobilePhone ?? "",
      use: parseMdmContactUse(company.mdmMobilePhoneUse),
    },
  };
}

function buildColumnLines(
  siret: string | undefined | null,
  profileLines: DisplayLine[],
): DisplayLine[] {
  const siretLine = optionalSiretLine(siret);
  return siretLine ? [siretLine, ...profileLines] : profileLines;
}

function CompanyInseeRefPersPanel({
  company,
  emailBrandLabel,
}: {
  company: RetailCompany;
  emailBrandLabel?: string | null;
}) {
  const refPersPresent = !company.mdmReferentialMissing;
  const inseeLines = buildColumnLines(
    company.inseeSiret,
    buildProfileDisplayLines(inseeProfileFromCompany(company), {
      includeIdentity: true,
    }),
  );
  const refPersLines = refPersPresent
    ? buildColumnLines(
        company.mdmSiret,
        buildProfileDisplayLines(refPersProfileFromCompany(company), {
          includeIdentity: true,
          emailBrandLabel: emailBrandLabel ?? undefined,
        }),
      )
    : [];

  return (
    <div className="bg-muted/40 space-y-4 rounded-lg border p-4 text-sm">
      <p className="font-medium">Société</p>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs font-semibold uppercase">
            INSEE
          </p>
          <ProfileReadOnlyLines lines={inseeLines} />
        </div>

        <div className="space-y-2">
          <p className="text-muted-foreground text-xs font-semibold uppercase">
            RefPers
          </p>
          {refPersPresent ? (
            <ProfileReadOnlyLines lines={refPersLines} />
          ) : (
            <p className="text-muted-foreground text-sm">
              Aucune information dans le Référentiel Personne (RefPers) pour ce
              SIRET.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { CompanyInseeRefPersPanel };
